import type { ReactNode } from "react";
import { Badge } from "../Badge/Badge";

type Props = {
  statusLabel: string;
  statusColor: string;
  /** Extra badges rendered after the status pill — e.g. a task's Aufgabentyp, or a
   *  "Braucht Umdisponierung" flag. */
  badges?: ReactNode;
  /** Right-aligned content on the status row — e.g. "Termin 2 / 3". */
  trailing?: ReactNode;
  /** e.g. "Behebungstermin" | "Termin". */
  primaryLabel: string;
  primaryValue: ReactNode;
  /** Overrides the default dark value colour — e.g. muted slate for "Nicht geplant". */
  primaryValueClassName?: string;
  /** A sub-line under the primary value — e.g. an Abgabefrist. */
  secondary?: ReactNode;
};

/**
 * The page's anchor card — status and the one date a technician standing at a door reads first.
 * Shared by both worker pages (moved here from
 * `pages/worker-appointment/ui/AppointmentStatusHeader.tsx`, which hard-coded "Behebungstermin"):
 * the appointment page passes its sequence number as `trailing`, the task page passes its
 * Aufgabentyp badge and Abgabefrist as `badges`/`secondary`.
 *
 * Replaces the bare `<p style={{ color }}>` status line and a disabled-input date field — status
 * is the most important word on the screen and used to carry the least visual weight of anything
 * on it.
 */
export const StatusHeaderCard = ({
  statusLabel,
  statusColor,
  badges,
  trailing,
  primaryLabel,
  primaryValue,
  primaryValueClassName,
  secondary,
}: Props) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={statusColor} label={statusLabel} dot />
        {badges}
      </div>
      {trailing}
    </div>

    <p className="mt-4 text-xs font-medium uppercase tracking-wide text-slate-500">
      {primaryLabel}
    </p>
    <p className={`mt-1 text-xl font-semibold ${primaryValueClassName ?? "text-slate-900"}`}>
      {primaryValue}
    </p>
    {secondary}
  </div>
);
